import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Instagram, Facebook, Phone, Mail, Share2, ArrowLeft } from "lucide-react";
import api, { fileUrl, img } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

export default function VendorDetail() {
  const { slug } = useParams();
  const { t, L } = useLang();
  const [v, setV] = useState(null);

  useEffect(() => {
    api.get(`/vendors/${slug}`).then((r) => setV(r.data)).catch(() => setV(false));
  }, [slug]);

  if (v === false) return <div className="max-w-3xl mx-auto px-6 py-20 font-mono-fk">Not found.</div>;
  if (!v) return <div className="max-w-3xl mx-auto px-6 py-20 font-mono-fk animate-pulse">Loading…</div>;

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: L(v.name_zh, v.name_en), url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success(L("链接已复制", "Link copied")); }
  };

  const social = v.social || {};
  const videos = (v.videos && v.videos.length ? v.videos : (v.video_url ? [v.video_url] : []));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/vendors" className="font-mono-fk text-sm inline-flex items-center gap-1 mb-6 hover:underline"><ArrowLeft size={15} /> {t("vendors_title")}</Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {v.photos?.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              <img src={fileUrl(v.photos[0])} alt="" className="col-span-2 rounded-2xl fk-border-thick w-full h-72 object-cover" />
              {v.photos.slice(1, 5).map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="rounded-xl fk-border h-36 w-full object-cover" />)}
            </div>
          ) : (
            <img src="https://images.unsplash.com/photo-1777846800324-0cbb1124fb9a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="" className="rounded-2xl fk-border-thick w-full h-72 object-cover" />
          )}

          {videos.length > 0 && (
            <div className="mt-6">
              <div className="font-mono-fk text-xs tracking-widest text-[#FF5757] mb-2">VIDEOS · {videos.length}</div>
              {videos.length === 1 ? (
                <video src={fileUrl(videos[0])} controls className="w-full rounded-2xl fk-border-thick" data-testid="vendor-video-0" />
              ) : (
                <Carousel className="w-full">
                  <CarouselContent>
                    {videos.map((vid, i) => (
                      <CarouselItem key={i}>
                        <video src={fileUrl(vid)} controls className="w-full rounded-2xl fk-border-thick" data-testid={`vendor-video-${i}`} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="fk-border bg-white" />
                  <CarouselNext className="fk-border bg-white" />
                </Carousel>
              )}
            </div>
          )}

          <h1 className="font-display text-4xl sm:text-5xl mt-8">{L(v.name_zh, v.name_en)}</h1>
          <span className="font-mono-fk text-xs fk-border rounded-full px-3 py-1 bg-[#FFD93D] inline-block mt-3">{v.category}</span>
          <p className="mt-5 text-black/70 leading-relaxed text-lg">{L(v.bio_zh, v.bio_en)}</p>

          {(v.latest_market_zh || v.latest_market_en) && (
            <div className="mt-6 bg-[#00E5FF]/20 fk-border rounded-2xl p-5">
              <div className="font-mono-fk text-xs tracking-widest mb-1">{L("最新市集资讯", "Latest market info")}</div>
              <p className="text-black/80">{L(v.latest_market_zh, v.latest_market_en)}</p>
            </div>
          )}
        </div>

        <aside className="bg-white fk-border-thick rounded-2xl p-6 fk-shadow h-fit space-y-3">
          <div className="font-mono-fk text-xs tracking-widest text-black/50">{t("contact")}</div>
          {v.contact_phone && <a href={`tel:${v.contact_phone}`} className="flex items-center gap-2 hover:text-[#FF5757]"><Phone size={16} /> {v.contact_phone}</a>}
          {v.contact_email && <a href={`mailto:${v.contact_email}`} className="flex items-center gap-2 hover:text-[#FF5757] break-all"><Mail size={16} /> {v.contact_email}</a>}
          {social.whatsapp && <a href={`https://wa.me/${social.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#25D366]"><MessageCircle size={16} /> WhatsApp</a>}
          {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#FF5757]"><Instagram size={16} /> Instagram</a>}
          {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#1877F2]"><Facebook size={16} /> Facebook</a>}
          <button onClick={share} data-testid="vendor-share" className="w-full mt-2 flex items-center justify-center gap-2 fk-border rounded-full px-4 py-2.5 font-semibold bg-[#FFD93D] fk-lift"><Share2 size={15} /> {t("share")}</button>
        </aside>
      </div>
    </div>
  );
}
